import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { SnackbarPayload, openSnackbar } from "../../../reducers/snackbar"
import { FEEDBACK_CHOICE_LABELS } from "../candidate-details/activity-timeline-column/activity-tabs/feedbackEventDisplay"
import { useDialogContainer } from "../utils"
import DialogHeader from "./DialogHeader"

export default function UpdateCreateCandidateFeedbackDialog({
  open,
  onClose,
  candidate,
}) {
  const [feedback, setFeedback] = useState("kudos_call")
  const [comments, setComments] = useState("")
  const [isSending, setIsSending] = useState(false)

  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()

  function handleSoftClose() {
    onClose()
  }

  function handleSendButtonClicked() {
    setIsSending(true)
    let submitFeedbackPromise
    const apiData = {
      feedback,
      comments,
    }
    if (candidate.feedback) {
      submitFeedbackPromise = axios.put(
        `/clients/api/candidate-feedback/${candidate.feedback.id}/`,
        apiData
      )
    } else {
      submitFeedbackPromise = axios.post("/clients/api/candidate-feedback/", {
        candidate: candidate.id,
        ...apiData,
      })
    }
    submitFeedbackPromise
      .then(({ data }) => onClose(data))
      .finally(() => setIsSending(false))
  }

  useEffect(() => {
    if (!candidate || !candidate.feedback) {
      setFeedback("kudos_call")
      setComments("")
    } else if (candidate.feedback) {
      setFeedback(candidate.feedback.feedback)
      setComments(candidate.feedback.comments)
    }
  }, [candidate])

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      maxWidth="md"
      fullWidth
    >
      <DialogHeader title="Send Feedback" onClose={handleSoftClose} />
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="label-send-feedback">Feedback</InputLabel>
            <Select
              label="Feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              labelId="label-send-feedback"
              required
            >
              {Object.entries(FEEDBACK_CHOICE_LABELS).map(([id, label]) => (
                <MenuItem key={id} value={id}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={handleSoftClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isSending}
          onClick={handleSendButtonClicked}
        >
          {candidate?.feedback ? "Update" : "Send"}
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}

export function useUpdateCreateCandidateFeedbackDialogState(onFeedbackUpdated) {
  const [candidateReceivingFeedback, setCandidateReceivingFeedback] =
    useState(null)
  const dispatch = useDispatch()

  function openUpdateCreateCandidateFeedbackDialog(e, candidate) {
    e.stopPropagation()
    setCandidateReceivingFeedback(candidate)
  }

  function closeUpdateCreateCandidateFeedbackDialog(feedback) {
    if (feedback) {
      dispatch(openSnackbar(new SnackbarPayload("Feedback sent successfully")))
      onFeedbackUpdated(feedback)
    }
    setCandidateReceivingFeedback(null)
  }

  return {
    candidateReceivingFeedback,
    updateCreateCandidateFeedbackDialogOpen: !!candidateReceivingFeedback,
    openUpdateCreateCandidateFeedbackDialog,
    closeUpdateCreateCandidateFeedbackDialog,
  }
}
